/**
 * External entry point for starting a client's Annual Review from outside
 * the app (sc-1347 item 1) — advisors kick this off from links dropped into
 * their HubSpot and Front queues, so this route has to work as a bare
 * "?identifier=..." landing rather than assuming any app-internal state.
 *
 * Resolves the target client (clientId direct, else a HubSpot-contact
 * lookup, else an email lookup — see `useResolveAnnualReviewStartClient`'s
 * doc comment for the precedence rationale) and hands off to the client
 * detail page with `?section=annual-review&action=start-annual-review`,
 * which `useAnnualReviewEntryAction` (wired into `AnnualReviewCard`) then
 * consumes to actually open the intake sheet / surface the right nudge.
 *
 * Gated behind the `annualReviews` LD flag same as the rest of the AR
 * surface — a link generated before the flag reaches an advisor's account
 * shouldn't dead-end silently, so this shows an explicit "not enabled" card
 * instead of resolving into a client page whose Annual Review section won't
 * even render.
 */
import * as React from 'react'
import { Link, createFileRoute, useNavigate } from '@tanstack/react-router'
import { z } from 'zod'
import { Loader2, Search, UserPlus } from 'lucide-react'
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { useLdSettled } from '@/hooks/use-ld-settled'
import { useFeatureFlags } from '@/hooks/use-feature-flags'
import { useResolveAnnualReviewStartClient } from '@/hooks/use-cases/useResolveAnnualReviewStartClient'
import { requestLinkNewClient } from '@/store/linkNewClientRequestStore'

const annualReviewStartSearchSchema = z.object({
  clientId: z.string().optional().catch(undefined),
  email: z.string().optional().catch(undefined),
  hubspotContactId: z.string().optional().catch(undefined),
})

export const Route = createFileRoute('/_app/annual-review/start')({
  validateSearch: annualReviewStartSearchSchema,
  component: AnnualReviewStartPage,
})

/** Shared centered-card shell for every state this landing can be in. */
function CenteredPane({ children }: { children: React.ReactNode }) {
  return (
    <div className="flex min-h-[60vh] items-center justify-center py-8">
      <div className="w-full max-w-md">{children}</div>
    </div>
  )
}

function OpeningSpinner() {
  return (
    <div className="flex flex-col items-center gap-3 py-8 text-center">
      <Loader2
        className="size-8 animate-spin text-muted-foreground"
        aria-hidden="true"
      />
      <p className="text-sm text-muted-foreground">
        Opening the Annual Review…
      </p>
    </div>
  )
}

function AnnualReviewStartPage() {
  const { clientId, email, hubspotContactId } = Route.useSearch()
  const navigate = useNavigate()
  const ldSettled = useLdSettled()
  const { annualReviews } = useFeatureFlags()
  const hasNavigatedRef = React.useRef(false)

  // Only feed real identifiers into the resolver once LD has settled AND the
  // flag is confirmed on — otherwise this fires HubSpot/email lookups for a
  // link that's about to dead-end on the "not enabled" card below anyway.
  const flagReady = ldSettled && Boolean(annualReviews)
  const resolution = useResolveAnnualReviewStartClient(
    flagReady ? { clientId, hubspotContactId, email } : {},
  )

  React.useEffect(() => {
    if (resolution.status !== 'found' || hasNavigatedRef.current) return
    hasNavigatedRef.current = true
    void navigate({
      to: '/clients/$clientId',
      params: { clientId: resolution.clientId },
      search: { section: 'annual-review', action: 'start-annual-review' },
      replace: true,
    })
  }, [resolution, navigate])

  if (!ldSettled) {
    return (
      <CenteredPane>
        <OpeningSpinner />
      </CenteredPane>
    )
  }

  if (!annualReviews) {
    return (
      <CenteredPane>
        <Card className="p-4">
          <CardHeader className="pb-3">
            <CardTitle className="text-lg">
              Annual Reviews aren&apos;t enabled for your account
            </CardTitle>
            <CardDescription>
              Ask your team lead to turn this on, then use the link again.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Button asChild className="h-11 w-full sm:h-9 sm:w-auto">
              {clientId ? (
                <Link to="/clients/$clientId" params={{ clientId }}>
                  Go to client
                </Link>
              ) : (
                <Link to="/clients">Go to clients</Link>
              )}
            </Button>
          </CardContent>
        </Card>
      </CenteredPane>
    )
  }

  if (resolution.status === 'no-identifier') {
    return (
      <CenteredPane>
        <Card className="p-4">
          <CardHeader className="pb-3">
            <CardTitle className="text-lg">Nothing to open</CardTitle>
            <CardDescription>
              This link is missing a client, HubSpot contact, or email to start
              the Annual Review for.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Button asChild className="h-11 w-full sm:h-9 sm:w-auto">
              <Link to="/clients">Go to clients</Link>
            </Button>
          </CardContent>
        </Card>
      </CenteredPane>
    )
  }

  if (resolution.status === 'not-found') {
    const identifierKind = hubspotContactId ? 'HubSpot contact' : 'email'
    const identifierValue = hubspotContactId ?? email ?? ''

    return (
      <CenteredPane>
        <Card className="p-4">
          <CardHeader className="pb-3">
            <CardTitle className="text-lg">
              No client matches this {identifierKind}
            </CardTitle>
            <CardDescription className="break-all">
              {identifierValue}
            </CardDescription>
          </CardHeader>
          <CardContent className="flex flex-col gap-2 sm:flex-row">
            <Button
              className="h-11 flex-1 gap-1.5 sm:h-9"
              onClick={() => {
                requestLinkNewClient(email ?? '')
                void navigate({ to: '/clients' })
              }}
            >
              <UserPlus className="size-4" />
              Link new client
            </Button>
            <Button
              type="button"
              variant="outline"
              className="h-11 flex-1 gap-1.5 sm:h-9"
              asChild
            >
              <Link to="/clients">
                <Search className="size-4" />
                Search clients
              </Link>
            </Button>
          </CardContent>
        </Card>
      </CenteredPane>
    )
  }

  // 'resolving' or 'found' (the redirect effect above fires on the same
  // render 'found' first appears — this just covers the tick until it does).
  return (
    <CenteredPane>
      <OpeningSpinner />
    </CenteredPane>
  )
}
